<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { useMenuV2 } from '@/composition/use-menu-v2';
  import { relativeTimeLabel } from '@/domain/store-setup/relative-time';
  import { menuCardModel } from '@/domain/store-setup/setup-dashboard';
  import type { IMenuCardAction } from '@/domain/store-setup/setup-dashboard';
  import type { IRestaurant } from '@/interfaces/IRestaurant';
  import type { IStoreSetupStatus } from '@/interfaces/IStoreSetup';

  /**
   * «Su menú» on the restaurant dashboard (plan §4.10), one row styled like
   * its neighbour widgets: a menu being read, a menu ready to review, the
   * published menu (dishes, categories, hidden dishes, view as a customer,
   * edit, update it from photos, and the latest update with «Deshacer»
   * while it can be undone) or, without dishes, how to load it. The page
   * does the navigation and the undo.
   */
  interface IStateButton extends IMenuCardAction {
    /** Outline instead of the solid orange of a next step. */
    quiet: boolean;
    icon?: string;
  }

  const { isMenuV2Active } = useMenuV2();
  const props = withDefaults(
    defineProps<{
      status: IStoreSetupStatus;
      restaurant: IRestaurant;
      /** Stores with several menus cannot use the AI import (§4.11). */
      multipleMenus?: boolean;
    }>(),
    { multipleMenus: false }
  );
  const emit = defineEmits<{
    (e: 'go', to: string): void;
    (e: 'view-as-diner'): void;
    /** «Deshacer» of the latest update from photos. */
    (e: 'undo-update', sessionId: string): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;
  const trc = (key: string, count: number, params?: Record<string, unknown>) =>
    String(proxy.$tc(key, count, params));

  const model = computed(() =>
    menuCardModel(
      props.status,
      props.restaurant.stringId,
      props.multipleMenus,
      isMenuV2Active.value
    )
  );
  /** The published menu, or null while reading, in review or empty. */
  const published = computed(() => {
    const current = model.value;
    return current.state === 'menu' ? current : null;
  });
  /** The single button of the other states. */
  const stateButton = computed<IStateButton | null>(() => {
    const current = model.value;
    if (current.state === 'menu') return null;
    return {
      ...current.action,
      // A running read only offers a look; review and load are next steps.
      quiet: current.state === 'reading',
      icon: current.state === 'empty' ? 'camera-outline' : undefined,
    };
  });
  const title = computed(() =>
    String(
      proxy.$t(
        model.value.state === 'empty'
          ? 'storeSetup.dashboard.emptyTitle'
          : 'storeSetup.dashboard.menuTitle'
      )
    )
  );
  /** «13 platos en 4 categorías · 1 oculto». */
  const summary = computed(() => {
    const menu = published.value;
    if (!menu) return '';
    const text = String(
      proxy.$t('storeSetup.dashboard.menuSummary', {
        dishes: trc('storeSetup.dashboard.menuDishes', menu.visibleItems, {
          count: menu.visibleItems,
        }),
        categories: trc(
          'storeSetup.dashboard.menuCategories',
          menu.categories,
          {
            count: menu.categories,
          }
        ),
      })
    );
    if (menu.hiddenItems === 0) return text;
    const hidden = trc('storeSetup.dashboard.menuHidden', menu.hiddenItems, {
      count: menu.hiddenItems,
    });
    return `${text} · ${hidden}`;
  });
  /** «Actualizado con fotos ayer». */
  const lastUpdateText = computed(() => {
    const lastUpdate = published.value?.lastUpdate;
    if (!lastUpdate) return null;
    return String(
      proxy.$t('storeSetup.dashboard.lastUpdate', {
        when: relativeTimeLabel(
          lastUpdate.publishedAt,
          new Date(),
          proxy.$i18n.locale
        ),
      })
    );
  });

  function editMenu() {
    if (published.value) emit('go', published.value.editTo);
  }

  function updateFromPhotos() {
    const to = published.value?.loadMoreTo;
    if (to) emit('go', to);
  }

  function runStateButton() {
    if (stateButton.value) emit('go', stateButton.value.to);
  }

  function undoUpdate() {
    const lastUpdate = published.value?.lastUpdate;
    if (lastUpdate) emit('undo-update', lastUpdate.sessionId);
  }
</script>

<template>
  <section class="v2-store-setup-menu-card">
    <div class="menu-body">
      <div class="menu-main">
        <PosUiIcon
          class="menu-icon"
          :name="
            model.state === 'empty' ? 'camera-outline' : 'silverware-fork-knife'
          "
          aria-hidden="true"
        />
        <div class="menu-copy">
          <div class="menu-heading">
            <h2 class="menu-title">{{ title }}</h2>
            <button
              v-if="published"
              type="button"
              class="menu-view"
              @click="emit('view-as-diner')"
            >
              <PosUiIcon
                name="eye-outline"
                class="view-icon"
                aria-hidden="true"
              />
              {{ $t('storeSetup.dashboard.viewAsDiner') }}
            </button>
          </div>
          <p v-if="model.state === 'reading'" class="menu-line is-reading">
            <span class="line-dot" aria-hidden="true" />
            {{ $t('storeSetup.dashboard.reading') }}
          </p>
          <p v-else-if="model.state === 'draft'" class="menu-line is-draft">
            <span class="line-dot" aria-hidden="true" />
            {{ $t('storeSetup.dashboard.draftReady') }}
          </p>
          <p v-else-if="model.state === 'empty'" class="menu-line">
            {{ $t('storeSetup.dashboard.emptyText') }}
          </p>
          <template v-else>
            <p class="menu-line">{{ summary }}</p>
            <p v-if="lastUpdateText" class="menu-line is-update">
              <PosUiIcon
                name="check-circle"
                class="update-icon"
                aria-hidden="true"
              />
              <span class="update-text">{{ lastUpdateText }}</span>
              <button type="button" class="update-undo" @click="undoUpdate">
                {{ $t('menuImport.undo') }}
              </button>
            </p>
          </template>
        </div>
      </div>
      <div class="menu-actions">
        <PosUiButton
          v-if="stateButton"
          class="menu-action"
          :color="stateButton.quiet ? 'neutral' : 'primary'"
          :variant="stateButton.quiet ? 'outline' : 'solid'"
          :icon="stateButton.icon"
          @click="runStateButton"
          >{{ $t(stateButton.labelKey) }}</PosUiButton
        >
        <template v-else-if="published">
          <PosUiButton
            class="menu-action"
            color="neutral"
            variant="outline"
            icon="pencil-outline"
            @click="editMenu"
            >{{ $t('storeSetup.dashboard.editMenu') }}</PosUiButton
          >
          <PosUiButton
            v-if="published.loadMoreTo"
            class="menu-action"
            variant="soft"
            icon="camera-outline"
            @click="updateFromPhotos"
            >{{ $t('storeSetup.dashboard.loadMore') }}</PosUiButton
          >
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
  // The surface, icon and title of the neighbour widgets (metric cards,
  // «Estado de Operaciones»). One row; the buttons wrap under the text when
  // the card is narrow and then share its width. Spacing uses margins, not
  // flex gap (unsupported on old POS devices, Chrome <84).
  .v2-store-setup-menu-card {
    @apply tw-rounded-lg tw-border tw-border-solid tw-border-neutral-100 tw-bg-white tw-px-4 tw-py-3.5 tw-shadow-sm;
    // 6 px margins on each side of the children make 12 px between them,
    // across and down; the negative margin cancels them at the card edge.
    .menu-body {
      @apply tw--m-1.5 tw-flex tw-flex-wrap tw-items-center;
    }
    // Takes nearly all the free space, so a wrapped actions row is alone on
    // its line and grows to the full width. The icon never leaves the text.
    .menu-main {
      @apply tw-m-1.5 tw-flex tw-min-w-0 tw-items-center;
      flex: 999 1 17rem;
    }
    .menu-icon {
      @apply tw-mr-3.5 tw-shrink-0 tw-text-orange-500;
      font-size: 32px;
    }
    .menu-copy {
      @apply tw-flex tw-min-w-0 tw-flex-1 tw-flex-col;
    }
    .menu-heading {
      @apply tw-flex tw-flex-wrap tw-items-baseline;
    }
    .menu-title {
      @apply tw-m-0 tw-mr-3 tw-text-base tw-font-semibold tw-text-neutral-800;
    }
    .menu-line {
      @apply tw-m-0 tw-mt-0.5 tw-flex tw-items-center tw-text-sm tw-text-neutral-600;
      .line-dot {
        @apply tw-mr-2 tw-h-2 tw-w-2 tw-shrink-0 tw-rounded-full;
      }
      &.is-reading {
        @apply tw-text-neutral-700;
        .line-dot {
          @apply tw-animate-pulse tw-bg-sky-500;
        }
      }
      &.is-draft {
        @apply tw-font-medium tw-text-amber-700;
        .line-dot {
          @apply tw-bg-amber-500;
        }
      }
      // Inline text, so the check stays with the words and «Deshacer»
      // wraps as one piece.
      &.is-update {
        @apply tw-block;
      }
      .update-icon {
        @apply tw-mr-1 tw-align-[-3px] tw-text-base tw-leading-none tw-text-green-600;
      }
      // The space before «Deshacer» ends the text, so it vanishes when
      // «Deshacer» wraps to the start of the next line.
      .update-text {
        @apply tw-mr-1.5;
      }
    }
    // Text links: a 44 px tall hit area without growing the line.
    .menu-view,
    .update-undo {
      @apply tw-relative tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-sm tw-underline tw-underline-offset-4;
      &::after {
        @apply tw-absolute tw--inset-x-1 tw--inset-y-3 tw-content-[''];
      }
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
    .menu-view {
      @apply tw-inline-flex tw-items-center tw-whitespace-nowrap tw-text-neutral-600 tw-decoration-neutral-300 hover:tw-text-neutral-900 hover:tw-decoration-current;
      .view-icon {
        @apply tw-mr-1 tw-text-base tw-leading-none tw-text-neutral-500;
      }
    }
    .update-undo {
      @apply tw-whitespace-nowrap tw-font-medium tw-text-neutral-900;
    }
    // May shrink below its buttons, so on the narrowest phones they wrap
    // onto a line each instead of overflowing the card. Its 2 px plus the
    // buttons' 4 px keep the same 12 px to the text.
    .menu-actions {
      @apply tw-m-0.5 tw-flex tw-min-w-0 tw-flex-wrap tw-justify-end;
      flex: 1 1 auto;
    }
    // Positioned, so the buttons paint above the text links' hit areas. The
    // minimum keeps a lone short button («Ver») from squeezing beside the
    // text on phones, like the other states.
    .menu-action {
      @apply tw-relative tw-m-1 tw-min-w-[7rem];
      flex: 1 1 auto;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-store-setup-menu-card .menu-line.is-reading .line-dot {
      @apply tw-animate-none;
    }
  }
</style>
